import React, { useEffect } from "react";
import { Button, FormInstance, notification } from "antd";
import Icon from "@mdi/react";
import { mdiMinus, mdiPlus } from "@mdi/js";
import { childAgeLabel, validateBooking } from "../../utils/helpers";
import { BookingInput } from "../../interfaces/booking.interface";
import { ChildAgeRanges } from "../../interfaces/itinerary.interface";

interface TravelersProps {
  travelerCounts: Record<string, number>;
  setTravelerCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  selectedTravelers: string[];
  setSelectedTravelers: React.Dispatch<React.SetStateAction<string[]>>;
  paxMultiplier: number;
  maxBookingSeats: number;
  pricingCategories: Record<string, string>;
  b2c2b: boolean;
  bookingForm?: FormInstance;
  bookingInputParams: BookingInput;
  childAgeRanges?: ChildAgeRanges;
}

const Travelers: React.FC<TravelersProps> = ({
  travelerCounts,
  setTravelerCounts,
  selectedTravelers,
  setSelectedTravelers,
  paxMultiplier,
  maxBookingSeats,
  pricingCategories,
  b2c2b,
  bookingForm,
  bookingInputParams,
  childAgeRanges,
}) => {
  const currentTotalPax = selectedTravelers.length;
  const [errorMessage, setErrorMessage] = React.useState("");

  const handleAddTraveler = (categoryKey: string) => {
    const count = travelerCounts[categoryKey] || 0;

    // Check if adding would exceed max booking seats
    if (selectedTravelers.length + paxMultiplier > maxBookingSeats) {
      notification.warning({
        message: "Maximum booking seats reached",
      });
      return;
    }

    setTravelerCounts((prevCounts) => ({
      ...prevCounts,
      [categoryKey]: count + paxMultiplier,
    }));

    setSelectedTravelers((prevTravelers) => {
      const newTravelers = Array(paxMultiplier).fill(categoryKey);
      return [...prevTravelers, ...newTravelers];
    });
  };

  const handleRemoveTraveler = (categoryKey: string) => {
    const count = travelerCounts[categoryKey] || 0;

    if (count > 0) {
      setTravelerCounts((prevCounts) => ({
        ...prevCounts,
        [categoryKey]: count - paxMultiplier,
      }));

      setSelectedTravelers((prevTravelers) => {
        const updatedTravelers = [...prevTravelers];
        let removed = 0;

        if(b2c2b){
          const passengers = bookingForm?.getFieldValue("passengers") || [];
          // Remove `paxMultiplier` entries for the category
          for (let i = updatedTravelers.length - 1; i >= 0 && removed < paxMultiplier; i--) {
            if (updatedTravelers[i] === categoryKey) {
              updatedTravelers.splice(i, 1);
              passengers.splice(i, 1);
              removed++;
            }
          }

          bookingForm?.setFieldsValue({
            ["passengers"]: [...bookingForm?.getFieldValue("passengers")],
          });
        }

        return updatedTravelers;
      });
    }
  };

  useEffect(() => {
    const validBooking = validateBooking(bookingInputParams, false);
    if(validBooking.isValid == false){
      setErrorMessage(validBooking.message);

      // // Clear error message after 10 seconds
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);

      return () => clearTimeout(timer);
    }
    setErrorMessage("");
  }, [bookingInputParams])

  return (
    <div className="travelers-content">
      {Object.entries(pricingCategories).map(([categoryKey, categoryLabel], index) => {
        const count = travelerCounts[categoryKey] || 0;
        const disableAdd = currentTotalPax === maxBookingSeats;
        const ageLabel = childAgeLabel(categoryKey, childAgeRanges);

        return (
          <div key={index} className="traveler-category">
            <div>
              <span className="traveler-category-label">{String(categoryLabel)}</span>
              {ageLabel && (
                <small>
                  <i>{ageLabel}</i>
                </small>
              )}
            </div>
            <div className="traveler-actions">
              <Button
                shape="circle"
                icon={<Icon path={mdiMinus} size="20px" />}
                type="primary"
                onClick={() => handleRemoveTraveler(categoryKey)}
                disabled={count === 0}
              />
              {count}
              <Button
                shape="circle"
                icon={<Icon path={mdiPlus} size="20px" />}
                type="primary"
                onClick={() => handleAddTraveler(categoryKey)}
                disabled={disableAdd}
              />
            </div>
          </div>
        );
      })}

      {!!errorMessage && (
        <div className="booking-note">
          <small>
            <i> {errorMessage} </i>
          </small>
        </div>
      )}
    </div>
  );
};

export default Travelers;
